import * as React from 'react';

import {
  ActivityStage,
  QuestionTypes,
} from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

import { FinishActivityEventAdapter } from '~analytics/adapters/FinishActivityEventAdapter';
import { FinishRoute } from '~analytics/types';
import { UIButton } from '~shared/components/buttons';
import { toast } from '~shared/components/Toast';
import { ShortAnswerLaunchDialog } from '~teacher/components/ActivityResult/modals/ShortAnswerLaunchDialog';
import { Room } from '~teacher/models/activities';
import { selectCurrentRoomActivity } from '~teacher/slices/teacher/teacherSelector';
import { currentRoomActivityChanged } from '~teacher/slices/teacher/teacherSlice';

import {
  finishActivity,
  legacyStartQuickQuestion,
} from '../../../api/activities/activityApi';
import { ActivityLaunchConfirmModal } from '../ActivityResult/modals/ActivityLaunchConfirmModal';

interface Props {
  room: Room;
  activityStage: ActivityStage;
}

const ResultsQuickLauncher = ({ room, activityStage }: Props) => {
  const { isLive, activityInstanceId } = useSelector(selectCurrentRoomActivity);
  const dispatch = useDispatch();
  const [isLaunchConfirmModalOpen, setIsLaunchConfirmModalOpen] =
    React.useState<boolean>(false);

  const [isShortAnswerLaunchDialogOpen, setIsShortAnswerLaunchDialogOpen] =
    React.useState<boolean>(false);

  const [questionTypeSelected, setQuestionTypeSelected] =
    React.useState<QuestionTypes>(null);

  const launchQuickQuestion = async ({
    questionType,
    allowRepeatResponses,
    requireNames,
    oneAttempt,
    questionText,
  }) => {
    try {
      if (isLive) {
        const finishedActivity = await finishActivity(activityInstanceId);

        if (finishedActivity.data) {
          new FinishActivityEventAdapter({
            isRosteredRoom: finishedActivity.data['isRostered'],
            settings: finishedActivity.data['settings'],
            activityType: finishedActivity.data['activityType'],
            numberStudents: finishedActivity.data['students'].length,
            numberResponses: finishedActivity.data['responses'].length,
            startTime: finishedActivity.data['startTime'],
            endTime: finishedActivity.data['endTime'],
            finishRoute: FinishRoute.StartNewActivity,
          }).sendAnalytics();
        }
      }

      const newActivityInstanceId: number = await legacyStartQuickQuestion({
        room: room,
        questionType: questionType,
        questionText: questionText,
        allowRepeatResponses: allowRepeatResponses,
        requireNames: requireNames,
        oneAttempt: oneAttempt,
      });

      if (newActivityInstanceId) {
        dispatch(
          currentRoomActivityChanged({
            activityInstanceId: newActivityInstanceId,
            isLive: true,
          })
        );
        window.SOCRATIVE.teacherRouter.routeToResultsQuestion(
          newActivityInstanceId,
          1,
          true
        );
      }
    } catch (error) {
      toast.critical(translate('An error occurred while trying to launch'));
      console.error(error);
    }
  };

  const launchQuickQuestionActivity = (questionType: QuestionTypes) => {
    switch (questionType) {
      case QuestionTypes.MultipleChoice:
      case QuestionTypes.TrueFalse:
        launchQuickQuestion({
          questionType: questionType,
          allowRepeatResponses: false,
          requireNames: false,
          oneAttempt: true,
          questionText: '',
        });
        return;
      case QuestionTypes.FreeResponse:
        setIsShortAnswerLaunchDialogOpen(true);
        return;
      default:
        console.error('Unknown question type');
    }
  };

  const onLaunchQuickQuestionClick = (questionType: QuestionTypes) => {
    if (activityStage === ActivityStage.InProgress) {
      setIsLaunchConfirmModalOpen(true);
      setQuestionTypeSelected(questionType);
      return;
    }
    launchQuickQuestionActivity(questionType);
  };

  return (
    <div data-testid="quick-launcher">
      <ActivityLaunchConfirmModal
        open={isLaunchConfirmModalOpen}
        onClose={() => {
          setIsLaunchConfirmModalOpen(false);
          setQuestionTypeSelected(null);
        }}
        onConfirmClick={() => {
          setIsLaunchConfirmModalOpen(false);
          launchQuickQuestionActivity(questionTypeSelected);
        }}
      />

      <ShortAnswerLaunchDialog
        open={isShortAnswerLaunchDialogOpen}
        onClose={() => setIsShortAnswerLaunchDialogOpen(false)}
        isRostered={room.isRostered}
        onConfirmClick={(launchData) => {
          return launchQuickQuestion({
            questionType: QuestionTypes.FreeResponse,
            ...launchData,
          });
        }}
      />

      <div className="py-20 mx-8">
        <p className="font-display text-blue-900 text-center text-md">
          {translate('Another Question')}
        </p>
        <div className="flex flex-wrap justify-center items-center mt-4">
          <div className="m-2">
            <UIButton
              className="bg-amber-50 text-amber-600 hover:bg-amber-100 focus:bg-amber-100 hover:text-amber-700 focus:text-amber-700"
              onClick={() =>
                onLaunchQuickQuestionClick(QuestionTypes.MultipleChoice)
              }
              appearance={null}
            >
              {translate('Multiple Choice')}
            </UIButton>
          </div>
          <div className="m-2">
            <UIButton
              className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100 focus:bg-indigo-100 hover:text-indigo-700 focus:text-indigo-700"
              onClick={() =>
                onLaunchQuickQuestionClick(QuestionTypes.TrueFalse)
              }
              appearance={null}
            >
              {translate('True / False')}
            </UIButton>
          </div>
          <div className="m-2">
            <UIButton
              className="bg-orange-50 text-orange-600 hover:bg-orange-100 focus:bg-orange-100 hover:text-orange-700 focus:text-orange-700"
              onClick={() =>
                onLaunchQuickQuestionClick(QuestionTypes.FreeResponse)
              }
              appearance={null}
            >
              {translate('Short Answer')}
            </UIButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ResultsQuickLauncher };
